import React, { useState } from "react";
import { useGetExitTicketGenerator } from "@packages/api/madlenAPI";
import { CheckCircle, TicketCheck, Expand, X } from "lucide-react";
import Markdown from "react-markdown";
import Text from "@core/text";
import { useTranslation } from "react-i18next";
import { ExitTicketGeneratorResponseDto } from "@packages/api/madlenAPI.schemas";
import { Button } from "@core/button";
import { ExitTicketCard } from "@features/tools/components/exit_tickets/ExitTicketCard";
import SpinnerLoader from "../components/SpinnerLoader";

interface ExitTicketsProps {
  id: number | null;
}

const ExitTicketsDetailComponent = ({
  project,
  closeModal,
}: {
  project: ExitTicketGeneratorResponseDto;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-auto bg-black bg-opacity-75 dark:bg-opacity-90 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <Text variant={"h1"}>
            {project.title || t("exitTickets.modalTitle")}
          </Text>
          <Button
            onClick={closeModal}
            variant="solid"
            size="icon"
            rounded="full"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={24} className="text-gray-500 dark:text-gray-400" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-6 modal-content">
          <ExitTicketCard
            courseName={project.courseName}
            createdAt={project.createdAt}
            exitTickets={project.exitTickets}
            isImageLoading={false}
            gradeLevel={project.gradeLevel}
            topic={project.title || project.prompt}
            imageUrl={project.imageUrl}
            videoUrl={project.videoUrl}
          />
        </div>
      </div>
    </div>
  );
};

const ExitTickets: React.FC<ExitTicketsProps> = ({ id }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError } = useGetExitTicketGenerator(id, {
    query: {
      refetchInterval: (data) => {
        if (
          data?.state.data?.exitTickets &&
          data.state.data.exitTickets.length > 0
        ) {
          return false;
        }
        return 2000;
      },
      enabled: !!id,
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 30,
    },
  });

  const isContentLoading =
    isLoading || !data?.exitTickets || data.exitTickets.length === 0;

  if (isContentLoading) {
    return <SpinnerLoader />;
  }

  if (isError) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <h3 className="text-xl font-medium mb-4 text-red-500 dark:text-red-400">
          {t("exitTickets.error.unableToLoad")}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("exitTickets.error.tryRegenerate")}
        </p>
      </div>
    );
  }

  const handleExpandClick = () => {
    setExpanded(true);
  };

  const closeModal = () => {
    setExpanded(false);
  };

  return (
    <>
      {expanded && data && (
        <ExitTicketsDetailComponent
          project={{
            ...data,
            gradeLevel: data.gradeLevel === -1 ? 0 : data.gradeLevel,
          }}
          closeModal={closeModal}
        />
      )}

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <div className="flex w-full justify-between items-center">
          <h3 className="text-xl font-medium mb-4 flex items-center justify-between text-gray-900 dark:text-gray-100">
            <div>{t("exitTickets.title")}</div>
          </h3>
          <button
            className="flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
            onClick={handleExpandClick}
          >
            <Expand size={18} className="text-gray-400 dark:text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          {data.exitTickets && data.exitTickets.length > 0 && (
            <div className="grid grid-cols-1 gap-6">
              {data.exitTickets.slice(0, 2).map((ticket, index) => (
                <div key={index} className="flex flex-col">
                  {ticket.ticket_title && (
                    <div className="flex flex-row items-center gap-2 text-gray-900 dark:text-gray-100 rounded-xl p-4">
                      <span className="p-2 rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-300">
                        <TicketCheck size={18} />
                      </span>
                      <h5 className="font-semibold text-lg">
                        {ticket.ticket_title}
                      </h5>
                    </div>
                  )}
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 space-y-4 flex-1 hover:bg-green-50 dark:hover:bg-green-900/20">
                    {ticket.ticket_prompt && (
                      <div className="prose dark:prose-invert max-w-none">
                        <Text
                          variant="p"
                          className="text-gray-800 dark:text-gray-200"
                        >
                          <Markdown>{ticket.ticket_prompt}</Markdown>
                        </Text>
                      </div>
                    )}
                    {ticket.recommended_format && (
                      <div className="mt-4">
                        <h6 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                          {t("exitTickets.recommendedFormat")}:
                        </h6>
                        <Text
                          variant="p"
                          size="sm"
                          className="mt-1 text-gray-700 dark:text-gray-300"
                        >
                          {ticket.recommended_format}
                        </Text>
                      </div>
                    )}
                    {ticket.possible_responses &&
                      ticket.possible_responses.length > 0 && (
                        <div className="mt-4">
                          <h6 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                            {t("exitTickets.possibleResponses")}:
                          </h6>
                          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 text-sm mt-1">
                            {ticket.possible_responses.map(
                              (response, index) =>
                                response && (
                                  <li
                                    key={index}
                                    className="flex items-start gap-2 flex-row"
                                  >
                                    <CheckCircle
                                      size={16}
                                      className="text-green-600 dark:text-green-300 flex-shrink-0 mt-1"
                                    />
                                    <Text
                                      variant="span"
                                      className="break-words text-gray-700 dark:text-gray-300"
                                    >
                                      {response.charAt(0).toUpperCase() +
                                        response.slice(1)}
                                    </Text>
                                  </li>
                                ),
                            )}
                          </ul>
                        </div>
                      )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ExitTickets;
